import AWS from "aws-sdk";
import S3 from "aws-sdk/clients/s3";
import ContentService from "./ContentService";
import { getAWSConfig } from "./../config";
import { LibraryObject } from "./../../model/library";
import {
  ASSETS_FOLDER_NAME,
  EDITOR_FILE_NAME,
  File as ContentFile,
  Folder,
} from "./../../model/content";
import { protectedPost } from "services/restUtil";
import { PreviewType } from "../../components/content/ContentActions/components/FolderMetadataDialog/FolderMetadataDialog";

export default class AWSContentService extends ContentService {
  bucket: string = "";
  s3Service: S3 | null = null;
  checkCredentialsPromise: Promise<object> | null = null;

  refreshToken(resolve: any, reject: any): void {
    getAWSConfig()
      .then((resp: any) => {
        AWS.config.credentials = new AWS.CognitoIdentityCredentials(
          {
            IdentityPoolId: resp.cognito.aws_cognito_identity_pool_id,
            IdentityId: resp.cognito.aws_cognito_identity_id,
            Logins: {
              "cognito-identity.amazonaws.com": resp.cognito.aws_cognito_token,
            },
          },
          {
            region: resp.region,
          }
        );
        AWS.config.update({
          region: resp.region,
        });
        this.bucket = resp.bucket;
        this.s3Service = new S3();
      })
      .then(resolve)
      .catch(reject);
  }

  checkCredentials(): Promise<object> {
    if (this.checkCredentialsPromise) {
      return this.checkCredentialsPromise;
    }

    this.checkCredentialsPromise = new Promise((resolve, reject) => {
      const resolveCheckCredentials = (obj: object) => {
        this.checkCredentialsPromise = null;
        resolve(obj);
      };
      const rejectCheckCredentials = () => {
        this.checkCredentialsPromise = null;
        reject();
      };

      if (!AWS.config.credentials) {
        this.refreshToken(resolveCheckCredentials, rejectCheckCredentials);
      } else {
        (AWS.config.credentials as AWS.CognitoIdentityCredentials).get(err => {
          if ((AWS!.config!.credentials! as AWS.CognitoIdentityCredentials).expireTime) {
            sessionStorage.setItem(
              "aws_token_expiration_time",
              (
                AWS!.config!.credentials! as AWS.CognitoIdentityCredentials
              ).expireTime.toISOString()
            );
          }
          if (err || (AWS.config.credentials as AWS.CognitoIdentityCredentials).expired) {
            this.refreshToken(resolveCheckCredentials, rejectCheckCredentials);
          } else {
            resolveCheckCredentials({});
          }
        });
      }
    });
    return this.checkCredentialsPromise;
  }

  getFolderContent(key: string, maxNumberOfKeys?: number): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }

          const params = {
            Bucket: this.bucket,
            Delimiter: "/",
          } as S3.Types.ListObjectsV2Request;
          if (key !== "") {
            params.Prefix = key;
          }
          if (maxNumberOfKeys) {
            params.MaxKeys = maxNumberOfKeys;
          }

          this.s3Service.listObjectsV2(params, (err, data) => {
            if (err) {
              reject(err && err.message ? err.message : "An error occurred.");
            } else {
              if (!data || !data.Contents) {
                reject("The data has invalid structure.");
                return;
              }
              const folders: Folder[] = [];
              if (data!.CommonPrefixes) {
                for (let i = 0; i < data!.CommonPrefixes.length; i++) {
                  const folder: Folder = {} as Folder;
                  folder.prefix = data!.CommonPrefixes[i].Prefix || "";
                  folder.key = folder.prefix;
                  const pathParts = folder.key.split("/");
                  folder.name = pathParts[pathParts.length - 2];
                  if (
                    folder.name.match(/^[\da-z]{32}$/i) ||
                    folder.name === ASSETS_FOLDER_NAME
                  ) {
                    // Hide folders with name as id string and 'assets' folder.
                    continue;
                  }
                  //folder.isPrivate = folder.name === ASSETS_FOLDER_NAME;
                  folders.push(folder);
                }
              }
              const files: ContentFile[] = [];
              if (data!.Contents) {
                for (let i = 0; i < data!.Contents.length; i++) {
                  const contentItem: S3.Object = data!.Contents[i];
                  if (contentItem.Key !== undefined && contentItem.Key.endsWith("/")) {
                    // Folders end with a slash.
                    continue;
                  }
                  const file: ContentFile = {} as ContentFile;
                  file.key = contentItem.Key || "";
                  file.lastModified = contentItem.LastModified;
                  file.prefix = key;
                  const pathParts = file.key.split("/");
                  file.name = pathParts[pathParts.length - 1];
                  if (file.name === EDITOR_FILE_NAME) {
                    // Hide editor.json file, that's an internal file.
                    continue;
                  }
                  //file.isPrivate = file.name === EDITOR_FILE_NAME;
                  files.push(file);
                }
              }
              resolve({
                folders: folders,
                files: files,
              });
            }
          });
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  private cleanMetadata(metadata: string | null | undefined): string | null | undefined {
    return metadata && metadata.trim() && metadata.replaceAll("\n", "");
  }

  private putObject({
    data,
    object,
    previewType,
    previewName,
    previewVerticalName,
    previewHorizontalName,
    folderKey,
    reject,
    resolve,
  }: {
    data: any;
    object: LibraryObject;
    previewType: PreviewType;
    previewName: string;
    previewVerticalName: string;
    previewHorizontalName: string;
    folderKey: string;
    reject: (value: any) => void;
    resolve: (err: any) => void;
  }) {
    if (object.preview) {
      this.s3Service!.putObject(
        {
          Bucket: this.bucket,
          Key: `${folderKey}${ASSETS_FOLDER_NAME}/${previewName}`,
          Body: object.preview,
        },
        (previewErr, previewData) => {
          if (previewErr) {
            reject(
              previewErr && previewErr.message ? previewErr.message : "An error occurred."
            );
          } else {
            resolve(previewData);
          }
        }
      );
    } else {
      resolve(data);
    }

    const secondPreviewType =
      previewType === PreviewType.VERTICAL
        ? PreviewType.HORIZONTAL
        : PreviewType.VERTICAL;
    const mainPreviewName =
      previewType === PreviewType.VERTICAL ? previewVerticalName : previewHorizontalName;
    const secondPreviewName =
      previewType === PreviewType.VERTICAL ? previewHorizontalName : previewVerticalName;

    if (object[previewType]) {
      this.s3Service!.putObject(
        {
          Bucket: this.bucket,
          Key: `${folderKey}${ASSETS_FOLDER_NAME}/${mainPreviewName}`,
          Body: object[previewType],
        },
        (previewErr, previewData) => {
          if (previewErr) {
            reject(
              previewErr && previewErr.message ? previewErr.message : "An error occurred."
            );
          } else {
            resolve(previewData);
          }
        }
      );
    } else {
      resolve(data);
    }
    if (object[secondPreviewType]) {
      this.s3Service!.putObject(
        {
          Bucket: this.bucket,
          Key: `${folderKey}${ASSETS_FOLDER_NAME}/${secondPreviewName}`,
          Body: object[secondPreviewType],
        },
        (previewErr, previewData) => {
          if (previewErr) {
            reject(
              previewErr && previewErr.message ? previewErr.message : "An error occurred."
            );
          } else {
            resolve(previewData);
          }
        }
      );
    } else {
      resolve(data);
    }
  }

  createFolder(object: LibraryObject, parentKey: string): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!object) {
            reject("No object.");
            return;
          }

          const metadata = {} as AWS.S3.Metadata;
          const author = this.cleanMetadata(object.author);
          if (author) {
            metadata.author = author;
          }
          const description = this.cleanMetadata(object.description);
          if (description) {
            metadata.description = description;
          }
          let previewName = "";
          let previewVerticalName = "";
          let previewHorizontalName = "";
          if (object.preview) {
            previewName = `${object.name}-preview.${object.preview.name.split(".")[1]}`;
            metadata.preview = previewName; // just a file name like preview.png
          }
          if (object.preview_horizontal) {
            previewHorizontalName = `${object.name}-horizontal-preview.${object.preview_horizontal.name.split(".")[1]}`;
            metadata.preview = previewHorizontalName; // just a file name like preview.png
          }
          if (object.preview_vertical) {
            previewVerticalName = `${object.name}vertical-preview.${object.preview_vertical.name.split(".")[1]}`;
            metadata.preview = previewVerticalName; // just a file name like preview.png
          }
          if (object.order) {
            metadata.order = "" + object.order;
          }
          if (object.length) {
            metadata.length = "" + object.length;
          }
          if (object.readings) {
            metadata.readings = "" + object.readings;
          }
          if (object.videos) {
            metadata.videos = "" + object.videos;
          }
          if (object.subtype) {
            metadata.subtype = "" + object.subtype;
          }
          const folderKey = `${parentKey}${object.name}/`;
          const params = {
            Bucket: this.bucket,
            Key: folderKey,
            Metadata: metadata,
          };

          this.s3Service.putObject(params, (err, data) => {
            if (err) {
              reject(err && err.message ? err.message : "An error occurred.");
            } else {
              const previewType: PreviewType = PreviewType.VERTICAL;
              this.putObject({
                data,
                object,
                previewName,
                previewVerticalName,
                previewHorizontalName,
                folderKey,
                previewType,
                reject,
                resolve,
              });
            }
          });
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  createFile(
    object: LibraryObject,
    parentKey: string,
    contentFiles: File[],
    assetFiles: File[]
  ): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }

          this.createFolder(object, parentKey)
            .then(() => {
              const folderKey = `${parentKey}${object.name}/`;
              let uploadedFilesNumber = 0;
              for (
                let contentFileIndex = 0;
                contentFileIndex < contentFiles.length;
                contentFileIndex++
              ) {
                const contentFile = contentFiles[contentFileIndex];
                this.s3Service!.putObject(
                  {
                    Bucket: this.bucket,
                    Key: `${folderKey}${contentFile.name}`,
                    Body: contentFile,
                    ContentType: contentFile.type,
                  },
                  // eslint-disable-next-line
                  (err, data) => {
                    uploadedFilesNumber++;
                    if (err) {
                      reject(err && err.message ? err.message : "An error occurred.");
                    } else if (uploadedFilesNumber === contentFiles.length) {
                      if (assetFiles.length > 0) {
                        for (let i = 0; i < assetFiles.length; i++) {
                          const assetFile = assetFiles[i];
                          this.s3Service!.putObject(
                            {
                              Bucket: this.bucket,
                              Key: `${folderKey}${ASSETS_FOLDER_NAME}/${assetFile.name}`,
                              Body: assetFile,
                            },
                            // eslint-disable-next-line
                            (fileErr, fileData) => {
                              uploadedFilesNumber++;
                              if (fileErr) {
                                reject(
                                  fileErr && fileErr.message
                                    ? fileErr.message
                                    : "An error occurred."
                                );
                              } else if (
                                uploadedFilesNumber ===
                                contentFiles.length + assetFiles.length
                              ) {
                                resolve(fileData);
                              }
                            }
                          );
                        }
                      } else {
                        resolve(data);
                      }
                    }
                  }
                );
              }
            })
            .catch(reject);
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  editFile(parentKey: string, contentFiles: File[], assetFiles: File[]): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }

          const folderKey = `${parentKey}/`;
          let uploadedFilesNumber = 0;
          for (
            let contentFileIndex = 0;
            contentFileIndex < contentFiles.length;
            contentFileIndex++
          ) {
            const contentFile = contentFiles[contentFileIndex];
            this.s3Service!.putObject(
              {
                Bucket: this.bucket,
                Key: `${folderKey}${contentFile.name}`,
                Body: contentFile,
                ContentType: contentFile.type,
              },
              // eslint-disable-next-line
              (err, data) => {
                uploadedFilesNumber++;
                if (err) {
                  reject(err && err.message ? err.message : "An error occurred.");
                } else if (uploadedFilesNumber === contentFiles.length) {
                  if (assetFiles.length > 0) {
                    for (let i = 0; i < assetFiles.length; i++) {
                      const assetFile = assetFiles[i];
                      this.s3Service!.putObject(
                        {
                          Bucket: this.bucket,
                          Key: `${folderKey}${ASSETS_FOLDER_NAME}/${assetFile.name}`,
                          Body: assetFile,
                        },
                        // eslint-disable-next-line
                        (fileErr, fileData) => {
                          uploadedFilesNumber++;
                          if (fileErr) {
                            reject(
                              fileErr && fileErr.message
                                ? fileErr.message
                                : "An error occurred."
                            );
                          } else if (
                            uploadedFilesNumber ===
                            contentFiles.length + assetFiles.length
                          ) {
                            resolve(fileData);
                          }
                        }
                      );
                    }
                  } else {
                    resolve(data);
                  }
                }
              }
            );
          }
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  getObjectMetadata(key: string): Promise<{ libraryObject: LibraryObject }> {
    return new Promise((resolve, reject) => {
      this.headObject(key)
        .then((data: S3.Types.HeadObjectOutput) => {
          if (!data || !data.Metadata) {
            reject("No metadata found.");
            return;
          }
          const libraryObject = new LibraryObject();
          let folderKey = key;
          if (folderKey.endsWith("/")) {
            folderKey = folderKey.substring(0, folderKey.length - 1);
          }
          const parts = folderKey.split("/");
          libraryObject.key = key;
          libraryObject.name = parts[parts.length - 1];
          if (data.Metadata.author) {
            libraryObject.author = decodeURIComponent(data.Metadata.author);
          }
          if (data.Metadata.description) {
            libraryObject.description = decodeURIComponent(data.Metadata.description);
          }
          if (data.Metadata.preview) {
            const previewURL = this.s3Service!.getSignedUrl("getObject", {
              Bucket: this.bucket,
              Key: `${key}${ASSETS_FOLDER_NAME}/${decodeURIComponent(data.Metadata.preview)}`,
            });
            libraryObject.preview_url = previewURL;
          }
          if (data.Metadata.preview_vertical) {
            const previewVerticalURL = this.s3Service!.getSignedUrl("getObject", {
              Bucket: this.bucket,
              Key: `${key}${ASSETS_FOLDER_NAME}/${decodeURIComponent(data.Metadata.preview_vertical)}`,
            });
            libraryObject.preview_vertical_url = previewVerticalURL;
          }
          if (data.Metadata.preview_horizontal) {
            const previewHorizontalURL = this.s3Service!.getSignedUrl("getObject", {
              Bucket: this.bucket,
              Key: `${key}${ASSETS_FOLDER_NAME}/${decodeURIComponent(data.Metadata.preview_horizontal)}`,
            });
            libraryObject.preview_horizontal_url = previewHorizontalURL;
          }
          if (data.Metadata.order) {
            libraryObject.order = parseInt(data.Metadata.order, 10);
          }
          if (data.Metadata.length) {
            libraryObject.length = parseInt(data.Metadata.length, 10);
          }
          if (data.Metadata.subtype) {
            libraryObject.subtype = decodeURIComponent(data.Metadata.subtype);
          }
          if (data.Metadata.readings) {
            libraryObject.readings = parseInt(data.Metadata.readings, 10);
          }
          if (data.Metadata.videos) {
            libraryObject.videos = parseInt(data.Metadata.videos, 10);
          }
          resolve({ libraryObject: libraryObject });
        })
        .catch(err => {
          reject(err);
        });
    });
  }

  headObject(key: string): Promise<S3.Types.HeadObjectOutput> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!key || !key.trim()) {
            reject("No key provided.");
            return;
          }
          this.s3Service.headObject(
            {
              Bucket: this.bucket,
              Key: key,
            },
            (err, data) => {
              if (err) {
                reject(err && err.message ? err.message : "An error occurred.");
              } else if (!data) {
                reject("No metadata found.");
              } else {
                resolve(data);
              }
            }
          );
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  getObject(key: string): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!key || !key.trim()) {
            reject("No key provided.");
            return;
          }
          this.s3Service.getObject(
            {
              Bucket: this.bucket,
              Key: key,
            },
            (err, data) => {
              if (err) {
                reject(err && err.message ? err.message : "An error occurred.");
              } else if (!data || !data.Body) {
                reject("No body received");
              } else {
                resolve(data);
              }
            }
          );
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  editFolderMetadata(object: LibraryObject): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!object) {
            reject("No object.");
            return;
          }
          let previewName = "";
          let previewVerticalName = "";
          let previewHorizontalName = "";
          const metadata = {} as AWS.S3.Metadata;
          const author = this.cleanMetadata(object.author);
          if (author) {
            metadata.author = author;
          }
          const description = this.cleanMetadata(object.description);
          if (description) {
            metadata.description = description;
          }
          if (object.preview) {
            previewName = `${object.name}-preview.${object.preview.name.split(".")[1]}`;
            metadata.preview = previewName; // just a file name like preview.png
          } else if (object.preview_url) {
            metadata.preview = object.preview_url.substring(
              object.preview_url.lastIndexOf("/") + 1,
              object.preview_url.indexOf("?")
            );
          }
          if (object.preview_vertical) {
            previewVerticalName = `${object.name}vertical-preview.${object.preview_vertical.name.split(".")[1]}`;
            metadata.preview_vertical = previewVerticalName; // just a file name like vertical-preview.png
          } else if (object.preview_vertical_url) {
            metadata.preview_vertical = object.preview_vertical_url.substring(
              object.preview_vertical_url.lastIndexOf("/") + 1,
              object.preview_vertical_url.indexOf("?")
            );
          }
          if (object.preview_horizontal) {
            previewHorizontalName = `${object.name}horizontal-preview.${object.preview_horizontal.name.split(".")[1]}`;
            metadata.preview_horizontal = previewHorizontalName; // just a file name like horizontal-preview.png
          } else if (object.preview_horizontal_url) {
            metadata.preview_horizontal = object.preview_horizontal_url.substring(
              object.preview_horizontal_url.lastIndexOf("/") + 1,
              object.preview_horizontal_url.indexOf("?")
            );
          }
          if (object.order) {
            metadata.order = "" + object.order;
          }
          if (object.length) {
            metadata.length = "" + object.length;
          }
          if (object.subtype) {
            metadata.subtype = "" + object.subtype;
          }
          if (object.readings) {
            metadata.readings = "" + object.readings;
          }
          if (object.videos) {
            metadata.videos = "" + object.videos;
          }
          const folderKey = object.key;
          const params = {
            Bucket: this.bucket,
            CopySource: this.bucket + "/" + folderKey,
            Key: folderKey,
            Metadata: metadata,
            MetadataDirective: "REPLACE",
          };
          this.s3Service.copyObject(params, (err, data) => {
            if (err) {
              reject(err && err.message ? err.message : "An error occurred.");
            } else {
              const previewType: PreviewType = PreviewType.HORIZONTAL;
              this.putObject({
                data,
                object,
                previewName,
                previewVerticalName,
                previewHorizontalName,
                folderKey,
                previewType,
                reject,
                resolve,
              });
            }
          });
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  renameFolder(object: LibraryObject): Promise<object> {
    return new Promise((resolve, reject) => {
      if (!object) {
        reject("No object.");
        return;
      }

      let folderKey = object.key;
      if (folderKey.endsWith("/")) {
        folderKey = folderKey.substring(0, folderKey.length - 1);
      }
      const folderParts = folderKey.split("/");
      if (folderParts[folderParts.length - 1] !== object.name) {
        folderParts[folderParts.length - 1] = object.name;
        const newKey = folderParts.join("/") + "/";

        this.moveFolder(object.key, newKey)
          .then(resp => resolve(resp))
          .catch(err => reject(err));
      } else {
        resolve({ message: "Nothing to change" });
        return;
      }
    });
  }

  removeFolder(key: string): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!key || !key.trim()) {
            reject("No key provided.");
            return;
          }

          this.s3Service.listObjects(
            {
              Bucket: this.bucket,
              Prefix: key,
            },
            (err, data) => {
              if (err) {
                reject(err && err.message ? err.message : "An error occurred.");
                return;
              }
              if (!data || !data.Contents) {
                reject("No data to delete");
                return;
              }
              const objects: S3.ObjectIdentifierList = data.Contents.map(object => {
                return { Key: object.Key } as S3.ObjectIdentifier;
              });
              this.s3Service!.deleteObjects(
                {
                  Bucket: this.bucket,
                  Delete: { Objects: objects, Quiet: true },
                },
                (deleteErr, deleteData) => {
                  if (deleteErr) {
                    reject(
                      deleteErr && deleteErr.message
                        ? deleteErr.message
                        : "An error occurred."
                    );
                  }
                  resolve(deleteData);
                }
              );
            }
          );
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  getAssetFileURL(parentKey: string, fileName: string): Promise<string> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }
          if (!parentKey || !parentKey.trim()) {
            reject("No key provided.");
            return;
          }
          const objectKey = `${parentKey}/${ASSETS_FOLDER_NAME}/${fileName}`;
          return this.s3Service.getSignedUrl(
            "getObject",
            {
              Bucket: this.bucket,
              Key: objectKey,
            },
            (err, url) => {
              if (err) {
                reject(err && err.message ? err.message : "An error occurred.");
                return;
              }
              resolve(url);
            }
          );
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  copyFolder(sourceFolderKey: string, targetFolderKey: string): Promise<object> {
    return new Promise((resolve, reject) => {
      this.checkCredentials()
        .then(() => {
          if (this.s3Service === null) {
            reject("Invalid configuration of s3 service.");
            return;
          }

          let sourceFolderKeyWithoutSlash = sourceFolderKey;
          if (sourceFolderKey.endsWith("/")) {
            sourceFolderKeyWithoutSlash = sourceFolderKey.substring(
              0,
              sourceFolderKey.length - 1
            );
          }
          const sourceFolderName = sourceFolderKeyWithoutSlash.substring(
            sourceFolderKeyWithoutSlash.lastIndexOf("/") + 1
          );

          this.s3Service.listObjectsV2(
            {
              Bucket: this.bucket,
              Prefix: sourceFolderKey,
            },
            (err, data) => {
              if (err) {
                reject(err && err.message ? err.message : "An error occurred.");
              } else {
                if (!data || !data.Contents) {
                  reject("The data has invalid structure.");
                  return;
                }
                let numberOfCopiedItems = 0;
                if (data.Contents) {
                  for (let i = 0; i < data!.Contents.length; i++) {
                    const contentItem: S3.Object = data!.Contents[i];
                    if (!contentItem.Key || contentItem.Key.endsWith(EDITOR_FILE_NAME)) {
                      // Here we don't copy editor.json file to student's folder.
                      // TODO it's better to have such file names in the function arguments and have this
                      // `copyFolder` function that just copies everything or skips some files if they are
                      // specified in the function argument. @talaeramel you will be able to use this function
                      // for Copy action for any folder then.
                      numberOfCopiedItems++;
                      continue;
                    }
                    let newContentItemKey = "";
                    if (contentItem.Key.endsWith(".xhtml")) {
                      // If teacher changed the folder name, make sure XHTML has the same name.
                      // @talaeramel this behaviour is specific for copying files from teacher's worksheets
                      // folder to student's assigned worksheets folder. For a general Copy action, you shouldn't
                      // do it.
                      newContentItemKey =
                        targetFolderKey +
                        sourceFolderName +
                        "/" +
                        sourceFolderName +
                        ".xhtml";
                    } else {
                      newContentItemKey = contentItem.Key.replace(
                        sourceFolderKey,
                        targetFolderKey + sourceFolderName
                      );
                    }
                    this.s3Service!.copyObject(
                      {
                        Bucket: this.bucket,
                        CopySource: this.bucket + "/" + contentItem.Key,
                        Key: newContentItemKey,
                      },
                      // eslint-disable-next-line
                      (copyErr /*, copyData*/) => {
                        if (copyErr) {
                          reject(
                            copyErr && copyErr.message
                              ? copyErr.message
                              : "An error occurred."
                          );
                        }
                        numberOfCopiedItems++;
                        if (numberOfCopiedItems === data!.Contents!.length) {
                          resolve({});
                        }
                      }
                    );
                  }
                }
              }
            }
          );
        })
        .catch(err => {
          reject(err || "Invalid credentials.");
        });
    });
  }

  moveFolder(sourceFolderKey: string, targetFolderKey: string): Promise<object> {
    return new Promise((resolve, reject) => {
      if (!sourceFolderKey.endsWith("/") || !targetFolderKey.endsWith("/")) {
        reject(
          `Expected folder path ending with '/', got: ${sourceFolderKey} and ${targetFolderKey}`
        );
        return;
      }
      protectedPost(
        "/admin/library/move",
        {
          from_prefix: sourceFolderKey,
          to_prefix: targetFolderKey,
        },
        resolve,
        reject
      );
    });
  }

  clearCredentials(): void {
    if (AWS.config.credentials) {
      AWS.config.credentials = null;
    }
  }
}
